import { graphQlManager } from "../services/server";
import {
    AspectType,
    GlobalPropertyConfiguration,
    GlobalPropertyState,
    GlobalPropertyStateAspectStorage,
} from "../types";
import { createGlobalPropertyStateMutation, updateGlobalPropertyStateMutation } from "./query";
import cloneDeep from "lodash.clonedeep";

export interface GlobalPropertyStateManager {
    getInitializationPromise(): Promise<void>;
    getGlobalPropertyState(): GlobalPropertyState | undefined;
    setGlobalPropertyState(state: GlobalPropertyState): Promise<void>;
    getAspect(name: string, channel?: number): string | undefined;
    getAspectStorage<S extends GlobalPropertyStateAspectStorage>(name: string, channel?: number): S | undefined;
    /**
     * Updates the value of a named aspect in the state.
     * @param name The name (key) of the aspect. This must match the key in the associated Global Property Config
     * @param value The value, represented as a string.
     * @param storage Optional: Additional data storage for the aspect. Not specifying this parameter, or providing `undefined`, will not update the
     * storage (if it already exists). Providing `null` will clear the existing storage.
     */
    setAspect(
        name: string,
        value: string,
        storage?: GlobalPropertyStateAspectStorage | null,
        channel?: number,
    ): Promise<void>;
    setAspectStorage<S extends GlobalPropertyStateAspectStorage>(
        name: string,
        storage: S | null,
        channel?: number,
    ): Promise<void>;
    setBundleOptions(bundleOptions?: BundleOptions): void;
    getBundleOptions(): BundleOptions | undefined;
    setConfiguration(configuration: GlobalPropertyConfiguration | undefined): void;
}

export interface BundleOptions {
    additionalHeaders?: { [key: string]: string };
    /**
     * If set to true, the products associated with the bundle will be eagerly fetched.
     */
    eagerFetchProducts?: boolean;

    bundleOwnerId?: string;

    /**
     * When reloading a bundle we may have an existing state to pull with it.
     */
    existingGlobalPropertyState?: GlobalPropertyState;

    readonly?: boolean;
}

export class GlobalPropertyStateManagerImpl implements GlobalPropertyStateManager {
    private readonly bundleId: string;
    private readonly bundleOwnerId?: string;
    private readonly initPromise: Promise<void>;
    private globalPropertyState?: GlobalPropertyState;
    private readonly onGlobalPropertyStateChange: (
        previousState: GlobalPropertyState,
        newState: GlobalPropertyState,
    ) => Promise<void>;
    private bundleOptions?: BundleOptions;
    private configuration?: GlobalPropertyConfiguration;

    constructor(
        bundleId: string,
        bundleOwnerId: string | undefined,
        configuration: GlobalPropertyConfiguration | undefined,
        onGlobalPropertyStateChange: (
            previousState: GlobalPropertyState,
            newState: GlobalPropertyState,
        ) => Promise<void>,
        bundleOptions?: BundleOptions,
    ) {
        this.bundleId = bundleId;
        this.bundleOwnerId = bundleOwnerId;
        this.configuration = configuration;
        this.bundleOptions = bundleOptions;

        const existingState = bundleOptions?.existingGlobalPropertyState;
        this.initPromise = existingState
            ? Promise.resolve(existingState).then(() => {
                  console.log(`Setting global property state to existing state.`);
                  this.globalPropertyState = existingState;
                  return undefined;
              })
            : this.createGlobalPropertyState().then((state) => {
                  console.log(`Setting global property state to new state.`);
                  this.globalPropertyState = state;
                  return undefined;
              });

        this.onGlobalPropertyStateChange = onGlobalPropertyStateChange;
    }

    setBundleOptions(bundleOptions?: BundleOptions) {
        this.bundleOptions = bundleOptions;
    }

    getBundleOptions() {
        return this.bundleOptions;
    }

    setConfiguration(configuration: GlobalPropertyConfiguration) {
        this.configuration = configuration;
    }

    getInitializationPromise(): Promise<void> {
        return this.initPromise;
    }

    getGlobalPropertyState(): GlobalPropertyState | undefined {
        return this.globalPropertyState;
    }

    async setGlobalPropertyState(state: GlobalPropertyState): Promise<void> {
        if (!this.globalPropertyState) {
            await this.initPromise;
        }
        const previousState = cloneDeep(this.globalPropertyState!);
        this.globalPropertyState = { ...state, id: previousState.id };
        await this.updateGlobalPropertyState();
        await this.onGlobalPropertyStateChange(previousState, this.globalPropertyState!);
    }

    getAspect(name: string, channel?: number): string | undefined {
        if (!this.globalPropertyState) {
            throw new Error("Global property state not initialized");
        }
        const aspect = this.globalPropertyState.aspects.find((a) => a.name === name && a.channel === channel);
        if (aspect) {
            return aspect.value;
        }
        return undefined;
    }

    getAspectStorage<S extends GlobalPropertyStateAspectStorage>(name: string, channel?: number): S | undefined {
        if (!this.globalPropertyState) {
            throw new Error("Global property state not initialized");
        }
        return this.globalPropertyState.aspects.find((a) => a.name === name && a.channel === channel)?.storage as
            | S
            | undefined;
    }

    async setAspect(
        name: string,
        value: string,
        storage?: GlobalPropertyStateAspectStorage | null,
        channel?: number,
    ): Promise<void> {
        if (!this.globalPropertyState) {
            throw new Error("Global property state not initialized");
        }
        if (!this.configuration) {
            throw new Error("Global property configuration not linked to the state manager.");
        }
        const configAspect = this.configuration.aspects.find((a) => a.name === name);
        if (!configAspect) {
            throw new Error(`Failed to find configuration aspect with name: ${name}`);
        }
        const previousState = cloneDeep(this.globalPropertyState);
        const aspects = this.globalPropertyState.aspects.filter((a) => a.name === name);
        if (aspects.length > 0) {
            if (channel) {
                const aspect = aspects.find((a) => a.channel === channel);
                if (aspect) {
                    aspect.value = value;
                    aspect.type = configAspect.type;
                    aspect.channel = channel;
                    if (storage !== undefined) {
                        aspect.storage = storage !== null ? storage : undefined;
                    }
                } else {
                    // We don't have an aspect for that channel.
                    if (channel === 1) {
                        // Find the aspect with undefined channel so that we can remove it because
                        // the aspect with channel 1 will serve that purpose.
                        const aspectIndex = this.globalPropertyState.aspects.findIndex((a) => !a.channel && a.name === name);
                        if (aspectIndex && aspectIndex > 0) {
                            this.globalPropertyState.aspects.splice(aspectIndex, 1);
                        }
                    }
                    this.globalPropertyState.aspects.push({
                        name,
                        value,
                        type: configAspect.type,
                        storage: storage !== null ? storage : undefined,
                        channel,
                    });
                }
            } else {
                const aspect = aspects[0];
                aspect.value = value;
                aspect.type = configAspect.type;
                if (storage !== undefined) {
                    aspect.storage = storage !== null ? storage : undefined;
                }
            }
        } else {
            this.globalPropertyState.aspects.push({
                name,
                value,
                type: configAspect.type,
                storage: storage !== null ? storage : undefined,
                channel,
            });
        }
        await this.updateGlobalPropertyState();
        await this.onGlobalPropertyStateChange(previousState, this.globalPropertyState);
    }

    async setAspectStorage(
        name: string,
        storage: GlobalPropertyStateAspectStorage | null,
        channel?: number,
    ): Promise<void> {
        if (!this.globalPropertyState) {
            throw new Error("Global property state not initialized");
        }
        if (!this.configuration) {
            throw new Error("Global property configuration not linked to the state manager.");
        }
        const configAspect = this.configuration.aspects.find((a) => a.name === name);
        if (!configAspect) {
            throw new Error(`Failed to find configuration aspect with name: ${name}`);
        }
        const previousState = cloneDeep(this.globalPropertyState);
        const aspects = this.globalPropertyState.aspects.filter((a) => a.name === name);
        if (aspects.length > 0) {
            if (channel) {
                const aspect = aspects.find((a) => a.channel === channel);
                if (aspect) {
                    aspect.storage = storage !== null ? storage : undefined;
                    aspect.type = configAspect.type;
                }
            } else {
                const aspect = aspects[0];
                aspect.storage = storage !== null ? storage : undefined;
                aspect.type = configAspect.type;
            }
        } else {
            this.globalPropertyState.aspects.push({
                name,
                value: "",
                type: configAspect.type,
                storage: storage !== null ? storage : undefined,
                channel,
            });
        }
        await this.updateGlobalPropertyState();
        await this.onGlobalPropertyStateChange(previousState, this.globalPropertyState);
    }

    private async updateGlobalPropertyState(): Promise<void> {
        if (!this.globalPropertyState) {
            throw new Error("Global property state not initialized");
        }
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ globalPropertyStateUpdate: GlobalPropertyState }>({
                mutation: updateGlobalPropertyStateMutation,
                variables: {
                    id: this.globalPropertyState.id,
                    aspects: this.globalPropertyState.aspects.map((aspect) => {
                        let storageInput = !!aspect.type && !!aspect.storage ? {} : undefined;
                        if (storageInput) {
                            switch (aspect.type) {
                                case AspectType.FileUpload: {
                                    storageInput["fileUpload"] = aspect.storage;
                                    break;
                                }
                                case AspectType.Option: {
                                    storageInput["option"] = aspect.storage;
                                    break;
                                }
                                case AspectType.ColorOption: {
                                    storageInput = aspect.storage;
                                    break;
                                }
                            }
                        }
                        return {
                            name: aspect.name,
                            value: aspect.value,
                            type: aspect.type,
                            storage: storageInput,
                            channel: aspect.channel,
                        };
                    }),
                },
                context: {
                    headers: {
                        bundleOwnerId: this.bundleOwnerId,
                        ...this.bundleOptions?.additionalHeaders,
                    },
                },
            });
        if (response.data?.globalPropertyStateUpdate) {
            this.globalPropertyState = response.data.globalPropertyStateUpdate;
        } else {
            throw new Error("Unable to update global property state");
        }
    }

    private async createGlobalPropertyState(): Promise<GlobalPropertyState> {
        const createResponse = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ globalPropertyStateCreate: GlobalPropertyState }>({
                mutation: createGlobalPropertyStateMutation,
                variables: {
                    bundleId: this.bundleId,
                },
                context: {
                    headers: {
                        bundleOwnerId: this.bundleOwnerId,
                        ...this.bundleOptions?.additionalHeaders,
                    },
                },
            });
        if (createResponse.data?.globalPropertyStateCreate && createResponse.data.globalPropertyStateCreate.id) {
            return createResponse.data.globalPropertyStateCreate;
        }
        throw new Error(`Unable to create global property state for bundle: ${this.bundleId}`);
    }
}
